import { LinkBuilder } from "./link-builder.service";

/**
 * Creates query strings for various query parameters
 */
export class QueryBuilder {
  private param: string;
  private equalityParam: string;

  private queryParams = {
    customersLink: 'customer.link ',
    entitlementDataCustomerLink: 'entitlementData.customer.link ',
    financialPeriod: 'financialPeriod.link ',
    financialPeriodsLink: 'financialPeriodLink.link ',
    location: 'location ',
    reimbursementRulesLink: 'reimbursementContract.link ',
    reportDatasetRequestsLink: 'reportViewRequest.link ',
    schemeLink: 'scheme.link ',
    schemesLink: 'schemes.link ',
    status: 'status ',
    transportOperatorsLink: 'operator.link ',
    transportOperatorServiceLink: 'service.link ',
    transportOperatorServicesLink: 'services.link ',
    type: 'type ',
    userId: 'userId ',
    not: 'not ',
    paymentTokenLink: 'paymentToken.link ',
    isLate: 'isLate '
  };

  private equalities = {
    positive: '= ',
    negative: '!= '
  };

  constructor(private linkBuilder: LinkBuilder) {
    this.newParam();
  }

  /**
   * Clears the param field and resets the equality param
   * @returns the query builder object
   */
  public newParam = () => {
    this.param = '';
    this.resetEqualityParam();
    return this;
  }

  /**
   * Returns whether the param has any entries
   * @returns true if param is empty string
   */
  public isParamEmpty = () => {
    return this.param === '';
  }

  /**
   * Gets the param and then resets it
   * @returns the constructed query
   */
  public getParam = () => {
    const finalParam = this.param;
    this.newParam();
    return finalParam;
  }

  /**
   * Adds and to the param then resets the equality param
   * @returns the query builder object
   */
  public and = () => {
    this.param += ' and ';
    this.resetEqualityParam();
    return this;
  }

  /**
   * Adds or to the param then resets the equality param
   * @returns the query builder object
   */
  public or = () => {
    this.param += ' or ';
    this.resetEqualityParam();
    return this;
  }

  /**
   * Sets the equality param to no equal
   * @returns the query builder object
   */
  public not = () => {
    this.equalityParam = this.equalities.negative;
    return this;
  }

  /**
   * Adds an opening bracket to the param
   * @returns the query builder object
   */
  public openSubQuery = () => {
    this.param += '( ';
    return this;
  }

  /**
   * Adds a closing bracket to the param
   * @returns the query builder object
   */
  public closeSubQuery = () => {
    this.param += ' )';
    return this;
  }

  /**
   * Adds fieldName greater than or equal to value to param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */
  public isGreaterOrEqualTo = (fieldName: string, value: string) => {
    this.param += fieldName + ' >= ' + value;
    return this;
  }

  /**
   *  Adds fieldName greater than value to param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */

  public isGreaterThan = (fieldName: string, value: string) => {
    this.param += fieldName + ' > ' + value;
    return this;
  }

  /**
   *  Adds fieldName less than value to param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */
  public isLessThan = (fieldName: string, value: string) => {
    this.param += fieldName + ' < ' + value;
    return this;
  }

  /**
   *  Adds fieldName less than or equal to value to param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */
  public isLessThanOrEqualTo = (fieldName: string, value: string) => {
    this.param += fieldName + ' <= ' + value;
    return this;
  }

  /**
   * Adds a contains value to the param
   *
   * @param value the value of the field
   * @returns the query builder object
   */
  public contains = (value: string) => {
    this.param += 'contains \'' + value + '\'';
    return this;
  }

  /**
   * Adds a fieldname containing value to the param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */
  public fieldContains = (fieldName: string, value: string) => {
    this.lexicalNot();
    this.param += fieldName + ' contains \'' + value + '\'';
    return this;
  }

  /**
   * Adds a fieldname value pair to the param
   *
   * @param fieldName the name of the field
   * @param value the value of the field
   * @returns the query builder object
   */
  public fieldIs = (fieldName: string, value: string) => {
    this.param += fieldName + ' ' + this.equalityParam + '\'' + value + '\'';
    return this;
  }

  /**
   * Adds a financial period link query to the param
   *
   * @param periodId the id of the period
   * @returns the query builder object
   */
  public financialPeriodIs = (periodId: string) => {
    this.param += this.queryParams.financialPeriod +
      this.equalityParam +
      this.linkBuilder.getFinancialPeriodsLink(periodId);
    return this;
  }

  /**
   * Adds a financial period link query to the param
   *
   * @param periodId the id of the period
   * @returns the query builder object
   */
  public financialPeriodLinkIs = (periodId: string) => {
    this.param += this.queryParams.financialPeriodsLink +
      this.equalityParam +
      this.linkBuilder.getFinancialPeriodsLink(periodId);
    return this;
  }

  /**
   * Adds an exists check on a field
   *
   * @param field the field to check
   * @returns the query builder object
   */
  public exists = (field: string) => {
    this.lexicalNot();
    this.param += 'exists ' + field;
    return this;
  }

  /**

   * @name idIs
   * Adds an id param check on a field
   *
   * @param id the id to check
   * @returns the query builder object
   */
  public idIs = (id: string) => {
    this.param += 'id ' + this.equalityParam + id;
    return this;
  }

  /**
   * Adds a operator link query to the param
   *
   * @param operatorId the id of the operator
   * @returns the query builder object
   */
  public operatorIs = (operatorId: string) => {
    this.param += this.queryParams.transportOperatorsLink +
      this.equalityParam +
      this.linkBuilder.getTransportOperatorsLink(operatorId);
    return this;
  }

  /**
   * Adds a reimbursement contract link query to the param
   *
   * @param contractId the id of the contract
   * @returns the query builder object
   */
  public reimbursementContractIs = (contractId: string) => {
    this.param += this.queryParams.reimbursementRulesLink +
      this.equalityParam +
      this.linkBuilder.getReimbursementRulesLink(contractId);
    return this;
  }

  /**
   * Adds a scheme link query to the param
   *
   * @param schemeId the id of the scheme
   * @returns the query builder object
   */
  public schemeIs = (schemeId: string) => {
    this.param += this.queryParams.schemeLink +
      this.equalityParam +
      this.linkBuilder.getSchemesLink(schemeId);
    return this;
  }

  /**
   * Adds a schemes link query to the param
   *
   * @param schemeId the id of the scheme
   * @returns the query builder object
   */
  public schemesIs = (schemeId: string) => {
    this.param += this.queryParams.schemesLink +
      this.equalityParam +
      this.linkBuilder.getSchemesLink(schemeId);
    return this;
  }

  /**
   * Adds a 'contains' query to the query param
   *
   * @param {string} fieldName the name of the field to query
   * @param {Array<string>} values the values to add to the contains param
   */
  public fieldIn = (fieldName: string, values: Array<string>) => {
    let query = '';

    for (let c = 0; c < values.length; c++) {
      query += '\'' + values[c] + '\'';

      if (c < values.length - 1) {
        query += ',';
      }
    }

    this.param += fieldName + ' in (' + query + ')'

    return this;
  }

  /**
   * Adds sort instructions to the param
   *
   * @param {Array<ISortInstruction>} sortArray the array of sort Instructions to add
   * @returns the query builder object
   */
  public sortBy = (sortArray: Array<InterACT.Interfaces.ISortInstruction>) => {
    const sortInstructions = sortArray.map((sortInstruction) => {
      return sortInstruction.columnName + ' ' + sortInstruction.direction;
    });
    this.param += ' sortBy ' + sortInstructions.join(', ');
    return this;
  }

  /**
   * Adds a location query to the param
   *
   * @param locationName the location to query against
   * @returns the query builder object
   */
  public locationIs = (locationName: string) => {
    this.param += this.queryParams.location + this.equalityParam + locationName;
    return this;
  }

  /**
   * Adds a status query to the param
   *
   * @param status the status value
   * @returns the query builder object
   */
  public statusIs = (status: string) => {
    this.param += this.queryParams.status + this.equalityParam + status;
    return this;
  }

  /**
   * Adds a service link query to the param
   *
   * @param serviceId the id of the service
   * @returns the query builder object
   */
  public transportOperatorServiceIs = (serviceId: string) => {
    this.param += this.queryParams.transportOperatorServiceLink +
      this.equalityParam +
      this.linkBuilder.getTransportOperatorServicesLink(serviceId);
    return this;
  }

  /**
   * Adds a services link query to the param
   *
   * @param serviceId the id of the service
   * @returns the query builder object
   */
  public transportOperatorServicesIs = (serviceId: string) => {
    this.param += this.queryParams.transportOperatorServicesLink +
      this.equalityParam +
      this.linkBuilder.getTransportOperatorServicesLink(serviceId);
    return this;
  }

  /**
   * Adds a type query to the param
   *
   * @param selectedType the type value
   * @returns the query builder object
   */
  public typeIs = (selectedType: string) => {
    this.param += this.queryParams.type + this.equalityParam + selectedType;
    return this;
  }

  /**
   * Adds a user id query to the param
   *
   * @param userId the id of the user
   * @returns the query builder object
   */
  public userIdIs = (userId: string) => {
    this.param += this.queryParams.userId + this.equalityParam + userId;
    return this;
  }

  /**
    * Adds a customer link query to the param
    *
    * @param {string} customerId the id of the customer
    * @returns {QueryBuilder} the query builder object
    */
  public customerLinkIs = (customerId: string) => {
    this.param += this.queryParams.customersLink +
      this.equalityParam +
      this.linkBuilder.getCustomersLink(customerId);
    return this;
  }

  /**
   * Adds an entitlement data customer link query to the param
   *
   * @param {string} customerId the id of the customer
   * @returns {QueryBuilder} the query builder object
   */
  public entitlementDataCustomerLinkIs = (customerId: string) => {
    this.param += this.queryParams.entitlementDataCustomerLink +
      this.equalityParam +
      this.linkBuilder.getCustomersLink(customerId);
    return this;
  }

  /**
   * Adds a financial period link query to the param
   *
   * @param paymentTokenId the id of the payment token
   * @returns the query builder object
   */
  public paymentTokenLinkIs = (paymentTokenId: string) => {
    this.param += this.queryParams.paymentTokenLink +
      this.equalityParam + paymentTokenId;
    return this;
  }

  /**
   * Adds is late check to param
   *
   * @returns the query builder object
   */
  public isLate = () => {
    this.param += this.queryParams.isLate;
    if (this.equalityParam === this.equalities.negative) {
      this.param += '< ';
    } else {
      this.param += '>= ';
    }
    this.param += 'true';
    return this;
  }

  /**
   * Resets the equality param to equal to.
   */
  private resetEqualityParam() {
    this.equalityParam = this.equalities.positive;
  }

  /**
   * Adds not to param if equalityParam is negative
   */
  private lexicalNot() {
    if (this.equalityParam === this.equalities.negative) {
      this.param += this.queryParams.not;
    }
  }
}
